import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { PageShell } from "@/components/layout/PageShell";
import { CampaignBanner } from "@/components/marketing/CampaignBanner";
import { SubscribeForm } from "@/components/marketing/SubscribeForm";
import { ContactForm } from "@/components/marketing/ContactForm";
import { FaqSection } from "@/components/marketing/FaqSection";
import { StewardshipDisclosure } from "@/components/marketing/StewardshipDisclosure";
import { WishlistGrid } from "@/components/marketing/WishlistGrid";
import { GoFundMeLogo } from "@/components/marketing/GoFundMeLogo";
import { WireDetailsPanel } from "@/components/marketing/WireDetailsPanel";
import { Seo } from "@/components/seo/Seo";
import { BreadcrumbSchema, CampaignSchema } from "@/components/seo/StructuredData";
import { faqsByCategory } from "@/data/faqs";
import {
  FOUNDATION_EMAIL,
  SUPPORT_TIERS,
  MOTTO,
  STEWARD_ENTITY,
  STEWARD_WIRE_PDF_PATH,
} from "@shared/campaign";
import { WISHLIST_ITEMS, WISHLIST_TOTAL_USD } from "@shared/wishlist";

// SHIP CHECKLIST — confirm Mercury ACH debit-block is enabled before deploy.
// Routing + account numbers surface via click-to-reveal in WireDetailsPanel.

const GIVE_LIVE: boolean =
  ((import.meta as unknown as { env?: Record<string, string> }).env?.VITE_GIVE_LIVE ?? "") === "true";

export function Contribute() {
  const [searchParams] = useSearchParams();
  const [wishItemId, setWishItemId] = useState<string | undefined>(undefined);

  // If a card linked the user here with ?wishItem=<id>, capture it once so
  // the contact form can submit it. The query param sticks; we just resolve
  // it to a known id and pass it down.
  useEffect(() => {
    const raw = searchParams.get("wishItem");
    if (!raw) return;
    const known = WISHLIST_ITEMS.find((i) => i.id === raw);
    if (known) setWishItemId(known.id);
  }, [searchParams]);

  const wishItem = wishItemId
    ? WISHLIST_ITEMS.find((i) => i.id === wishItemId)
    : undefined;

  return (
    <PageShell
      title="Get involved"
      subtitle="Three ways to support the .person Foundation. Each is equally welcome."
      stewardship="Contribute"
      width="wide"
    >
      <Seo
        title="Get involved"
        description="Support the .person Foundation. Direct a gift to a Wish List item, contribute generally to the Stewardship Account, join the engineering effort, or offer advisory expertise."
        path="/foundation/contribute"
      />
      <BreadcrumbSchema
        items={[
          { name: "Home", path: "/" },
          { name: "Foundation", path: "/foundation" },
          { name: "Contribute", path: "/foundation/contribute" },
        ]}
      />
      <CampaignSchema />

      {/* Campaign banner (inline variant) */}
      <section className="mb-12" aria-labelledby="fund-heading">
        <h2 id="fund-heading" className="sr-only">Fund the ICANN application</h2>
        <CampaignBanner variant="inline" />
      </section>

      {/* Stewardship disclosure — read this before any direct-give CTA */}
      <section className="mb-14">
        <StewardshipDisclosure />
      </section>

      {/* Three pillars */}
      <section
        aria-labelledby="pillars-heading"
        className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16"
      >
        <h2 id="pillars-heading" className="sr-only">Three ways to contribute</h2>

        <article className="border border-hairline p-8 flex flex-col bg-paper hover:shadow-sm transition-shadow">
          <span className="font-mono text-[10px] uppercase tracking-widest text-claude mb-4 block font-bold">
            01 / Donate
          </span>
          <h3 className="text-2xl font-serif text-ink mb-4">Funders &amp; philanthropists</h3>
          <p className="text-sm text-ink-2 mb-6 leading-relaxed flex-1">
            The Stewardship Account is the primary path for monetary support.
            Direct your gift to a specific Wish List item below, or contribute
            generally with the memo code <span className="font-mono">DPF-GENERAL</span>.
          </p>
          <ul className="text-xs font-mono text-ink-2 mb-6 space-y-1">
            {SUPPORT_TIERS.map((t) => (
              <li
                key={t.label}
                className="flex justify-between border-b border-hairline py-1"
              >
                <span className="uppercase tracking-widest text-ink-3">{t.label}</span>
                <span className="text-ink">${t.amount_usd.toLocaleString()}+</span>
              </li>
            ))}
          </ul>
          <div className="flex flex-col gap-3">
            <a
              href="#wishlist"
              className="inline-block text-xs uppercase tracking-widest font-semibold text-paper bg-ink py-3 text-center hover:bg-ink-2 transition-colors"
            >
              Direct your gift ↓
            </a>
            <a
              href={STEWARD_WIRE_PDF_PATH}
              download
              className="inline-block text-xs uppercase tracking-widest font-semibold text-ink border border-ink py-3 text-center hover:bg-paper-2 transition-colors"
            >
              Wire details (PDF)
            </a>
          </div>
        </article>

        <article className="border border-hairline p-8 flex flex-col bg-paper hover:shadow-sm transition-shadow">
          <span className="font-mono text-[10px] uppercase tracking-widest text-state-active mb-4 block font-bold">
            02 / Build
          </span>
          <h3 className="text-2xl font-serif text-ink mb-4">Engineers</h3>
          <p className="text-sm text-ink-2 mb-6 leading-relaxed flex-1">
            The Protocol needs careful engineering across decentralised identity, memory
            storage, zero-knowledge circuits, and cross-surface adapters. Contributions are
            welcome under Apache 2.0 and CC BY 4.0.
          </p>
          <a
            href="https://github.com/dotperson"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block text-xs uppercase tracking-widest font-semibold text-ink border border-ink py-3 text-center hover:bg-ink hover:text-paper transition-colors"
          >
            View the open repository
          </a>
        </article>

        <article className="border border-hairline p-8 flex flex-col bg-paper hover:shadow-sm transition-shadow">
          <span className="font-mono text-[10px] uppercase tracking-widest text-state-pending mb-4 block font-bold">
            03 / Advise
          </span>
          <h3 className="text-2xl font-serif text-ink mb-4">Critics &amp; advisors</h3>
          <p className="text-sm text-ink-2 mb-6 leading-relaxed flex-1">
            Philosophers, AI ethicists, civil-rights lawyers, theologians, and Ubuntu
            scholars shape the Governance layer. Critique is especially welcome — the
            Protocol should be stress-tested before it ships, not after.
          </p>
          <a
            href={`mailto:${FOUNDATION_EMAIL}?subject=Advisory%20Council`}
            className="inline-block text-xs uppercase tracking-widest font-semibold text-ink border border-ink py-3 text-center hover:bg-ink hover:text-paper transition-colors"
          >
            Offer expertise
          </a>
        </article>
      </section>

      {/* GoFundMe — public crowdfunding rail for the .person campaign */}
      <section
        id="gofundme"
        aria-labelledby="gofundme-heading"
        className="border border-hairline bg-paper p-6 md:p-8 mb-16 flex flex-col md:flex-row gap-6 md:gap-8 items-start md:items-center scroll-mt-24"
      >
        <a
          href="https://gofund.me/7b4dde3da"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Open the GoFundMe campaign"
          className="flex-shrink-0 text-[#4A9D44] hover:opacity-80 transition-opacity"
        >
          <GoFundMeLogo className="w-[120px] h-auto" />
        </a>
        <div className="flex-1 min-w-0">
          <span className="font-mono text-[10px] uppercase tracking-widest text-[#4A9D44] mb-2 block font-bold">
            Public crowdfunding
          </span>
          <h2
            id="gofundme-heading"
            className="text-xl md:text-2xl font-serif text-ink mb-2 leading-tight"
          >
            Support the campaign on GoFundMe
          </h2>
          <p className="text-sm text-ink-2 leading-relaxed mb-4">
            For supporters who&rsquo;d rather contribute through crowdfunding
            rails. Funds flow into the same {STEWARD_ENTITY} stewardship
            account as wire and card gifts.
          </p>
          <a
            href="https://gofund.me/7b4dde3da"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block text-xs uppercase tracking-widest font-semibold text-paper bg-[#4A9D44] px-5 py-2.5 hover:opacity-90 transition-opacity"
          >
            Give on GoFundMe →
          </a>
        </div>
      </section>

      {/* Wish List */}
      <section
        id="wishlist"
        aria-labelledby="wishlist-heading"
        className="border-t border-hairline pt-14 mb-16 scroll-mt-24"
      >
        <div className="mb-8 max-w-3xl">
          <span className="font-mono text-[10px] uppercase tracking-widest text-claude mb-2 block font-bold">
            Directed gifts
          </span>
          <h2
            id="wishlist-heading"
            className="text-2xl md:text-3xl font-serif text-ink mb-3"
          >
            Wish List
          </h2>
          <p className="text-sm md:text-base text-ink-2 font-serif leading-relaxed">
            Specific things the Foundation needs &mdash; from a US$220 keyboard
            up to a US$7,200 sponsored Social Media Manager. Fully fund any
            item, or contribute partially with the memo code shown on the
            card. Hardware items can also be purchased directly from the
            vendor and shipped to the steward&rsquo;s Delaware address.
          </p>
          <p className="text-xs font-mono text-ink-3 mt-3">
            {WISHLIST_ITEMS.length} items · US$
            {WISHLIST_TOTAL_USD.toLocaleString("en-US")} total
          </p>
        </div>
        <WishlistGrid />
      </section>

      {/* Generic stewardship-account block (no specific item directed) */}
      <section
        id="general-gift"
        aria-labelledby="general-gift-heading"
        className="border-t border-hairline pt-14 mb-16"
      >
        <div className="mb-6 max-w-3xl">
          <span className="font-mono text-[10px] uppercase tracking-widest text-ink-3 mb-2 block">
            Or give without directing
          </span>
          <h2
            id="general-gift-heading"
            className="text-2xl font-serif text-ink mb-3"
          >
            Stewardship Account · general support
          </h2>
          <p className="text-sm text-ink-2 font-serif leading-relaxed">
            For supporters who would rather let the Foundation deploy the gift
            where it&rsquo;s most needed. Funds flow into the same{" "}
            {STEWARD_ENTITY} stewardship account as Wish List directed gifts;
            allocation is reported on the public ledger.
          </p>
        </div>
        {GIVE_LIVE && (
          <div className="mb-6">
            <Link
              to="/give/general"
              className="inline-block text-xs uppercase tracking-widest font-semibold bg-claude text-paper px-5 py-2.5 hover:bg-claude/90 transition-colors"
            >
              Pay by card →
            </Link>
          </div>
        )}
        <WireDetailsPanel notifyHref="#contact" />
      </section>

      {/* Grant strategy */}
      <section
        id="grant-strategy"
        aria-labelledby="grant-strategy-heading"
        className="border-t border-hairline pt-14 mb-16"
      >
        <div className="mb-6">
          <span className="font-mono text-[10px] uppercase tracking-widest text-ink-3 mb-2 block">
            Non-dilutive funding
          </span>
          <h2 id="grant-strategy-heading" className="text-2xl font-serif text-ink mb-3">
            Grant strategy
          </h2>
          <p className="text-sm text-ink-2 font-serif max-w-2xl leading-relaxed">
            The Foundation is built to be funded without equity, without
            acquisition, and without strings. The Stewardship Account closes
            the ICANN gap; the grant pipeline below funds the years after.
            Source: <em>Person Foundation Funding Roadmap</em>.
          </p>
        </div>

        <div className="border border-hairline overflow-x-auto">
          <table className="w-full text-sm font-sans">
            <thead>
              <tr className="border-b border-hairline bg-paper-2 text-[10px] font-mono uppercase tracking-widest text-ink-3">
                <th scope="col" className="text-left px-5 py-3 font-bold">
                  Programme
                </th>
                <th scope="col" className="text-left px-5 py-3 font-bold">
                  Award
                </th>
                <th scope="col" className="text-left px-5 py-3 font-bold">
                  Fit
                </th>
                <th scope="col" className="text-left px-5 py-3 font-bold">
                  Window
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-hairline text-ink-2">
              <tr className="bg-paper">
                <th scope="row" className="text-left px-5 py-4 font-serif text-ink font-medium">
                  AWS Imagine Grant{" "}
                  <span className="block text-[10px] font-mono uppercase tracking-widest text-claude mt-1">
                    Highest-value
                  </span>
                </th>
                <td className="px-5 py-4 font-mono text-xs">
                  $200K cash + $100K credits
                </td>
                <td className="px-5 py-4 text-sm">
                  Pathfinder GenAI track. Funds 12 months of team work.
                </td>
                <td className="px-5 py-4 font-mono text-xs">
                  Post-501(c)(3); 2026–27 cycle
                </td>
              </tr>
              <tr>
                <th scope="row" className="text-left px-5 py-4 font-serif text-ink font-medium">
                  Google for Startups Cloud (AI)
                </th>
                <td className="px-5 py-4 font-mono text-xs">
                  Up to $350K credits
                </td>
                <td className="px-5 py-4 text-sm">
                  Inference, indexing, and memory-layer infrastructure for the
                  reference runtime.
                </td>
                <td className="px-5 py-4 font-mono text-xs">Rolling</td>
              </tr>
              <tr>
                <th scope="row" className="text-left px-5 py-4 font-serif text-ink font-medium">
                  Polygon Community Grants S2
                </th>
                <td className="px-5 py-4 font-mono text-xs">~$10K–$30K</td>
                <td className="px-5 py-4 text-sm">
                  Genesis Layer on Polygon PoS (Phase 1 identity anchor).
                </td>
                <td className="px-5 py-4 font-mono text-xs">Open season</td>
              </tr>
              <tr>
                <th scope="row" className="text-left px-5 py-4 font-serif text-ink font-medium">
                  Filecoin Foundation · Open Grants
                </th>
                <td className="px-5 py-4 font-mono text-xs">~$5K–$25K</td>
                <td className="px-5 py-4 text-sm">
                  Memory Layer on IPFS/Filecoin. Shard persistence and provenanced
                  retrieval.
                </td>
                <td className="px-5 py-4 font-mono text-xs">Rolling</td>
              </tr>
              <tr>
                <th scope="row" className="text-left px-5 py-4 font-serif text-ink font-medium">
                  Ethereum Foundation · ESP
                </th>
                <td className="px-5 py-4 font-mono text-xs">Programme-dependent</td>
                <td className="px-5 py-4 text-sm">
                  Ecosystem support for decentralised-identity and personhood
                  primitives.
                </td>
                <td className="px-5 py-4 font-mono text-xs">Rolling</td>
              </tr>
              <tr>
                <th scope="row" className="text-left px-5 py-4 font-serif text-ink font-medium">
                  Ford · MacArthur · OSF · Knight · Mozilla · Omidyar
                </th>
                <td className="px-5 py-4 font-mono text-xs">Institutional, varied</td>
                <td className="px-5 py-4 text-sm">
                  Philanthropic foundations working at the intersection of digital
                  rights, civil society, and technology ethics.
                </td>
                <td className="px-5 py-4 font-mono text-xs">Months 6–12</td>
              </tr>
            </tbody>
          </table>
        </div>

        <p className="mt-6 text-xs text-ink-3 font-mono max-w-2xl leading-relaxed">
          Total non-dilutive potential: <span className="text-ink">US$500K–$850K+</span>{" "}
          across programmes. Grant sequencing depends on 501(c)(3) determination and
          TechSoup registration. If you can introduce the Foundation to a programme
          officer at any of these, please{" "}
          <a
            href={`mailto:${FOUNDATION_EMAIL}?subject=Grant%20introduction`}
            className="text-ink underline decoration-hairline hover:decoration-ink"
          >
            write to us
          </a>
          .
        </p>
      </section>

      {/* Contact form */}
      <section
        id="contact"
        aria-labelledby="contact-heading"
        className="border-t border-hairline pt-14 mb-14 scroll-mt-24"
      >
        <div className="max-w-2xl">
          {wishItem && (
            <div className="border border-hairline bg-paper-2 p-4 mb-6 text-sm font-mono text-ink-2">
              <span className="text-[10px] uppercase tracking-widest text-claude font-bold mr-2">
                Directed gift
              </span>
              You are notifying the Foundation about a gift directed to{" "}
              <strong className="text-ink">{wishItem.name}</strong>. The
              memo code is{" "}
              <span className="text-ink">DPF-WISH-{wishItem.id.toUpperCase()}</span>.
            </div>
          )}
          <ContactForm
            inquiryType={wishItem ? "funding" : "general"}
            wishItemId={wishItem?.id}
            wishItemName={wishItem?.name}
            heading={
              wishItem
                ? "Notify us your gift is on the way"
                : "Contact the Foundation"
            }
            successMessage="Thank you. Your message is with us. A confirmation is on its way to your inbox, and we'll be in touch shortly."
          />
          <p className="mt-6 text-xs font-mono text-ink-3">
            Or email us directly at{" "}
            <a
              href={`mailto:${FOUNDATION_EMAIL}`}
              className="text-ink underline decoration-hairline hover:decoration-ink"
            >
              {FOUNDATION_EMAIL}
            </a>
            .
          </p>
        </div>
      </section>

      {/* Subscribe */}
      <section
        id="subscribe"
        aria-labelledby="subscribe-heading"
        className="border-t border-hairline pt-14 text-center mb-14"
      >
        <h2 id="subscribe-heading" className="text-2xl font-serif text-ink mb-4">
          Stay updated
        </h2>
        <p className="text-ink-2 font-serif mb-8 max-w-md mx-auto">
          Protocol releases, essays from the Journal, and Foundation status updates — no more
          than once a fortnight.
        </p>
        <SubscribeForm source="contribute-page" />
      </section>

      <FaqSection
        faqs={faqsByCategory("campaign")}
        heading="About the campaign"
        id="contribute-faq"
      />

      <p className="mt-14 font-mono text-[10px] uppercase tracking-[0.25em] text-claude text-center">
        {MOTTO}
      </p>
    </PageShell>
  );
}
